import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquare, CheckCircle2, Bell } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { saveUnlock } from "@/lib/unlock";
import { CommentField } from "@/components/CommentField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inputCls = "bg-slate-950 border-slate-800 focus:border-brand text-slate-100 placeholder:text-slate-600 h-10";

export function QuickLeadForm({ dealer, vehicle, onLead }) {
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: lead } = await api.post("/public/leads", {
        vehicle_id: vehicle.id,
        dealer_id: dealer.id,
        source: "vehicle_quick",
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        comment: form.comment,
        message: `Asked to be texted about the ${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock ${vehicle.stock_number || "n/a"}).`,
      });
      saveUnlock(dealer.id, lead);
      onLead?.(lead);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quick-lead-success" className="rounded-2xl border border-emerald-800/50 bg-emerald-950/30 p-5 flex items-center gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" />
        <div>
          <div className="font-semibold">Got it, {form.customer_name.split(" ")[0]}!</div>
          <div className="text-xs text-slate-400">{dealer.name} will text you about this {vehicle.model} shortly.</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quick-lead-form" className="rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-5">
      <div className="flex items-center gap-2 text-sm font-semibold mb-1">
        <MessageSquare className="h-4 w-4 text-brand" /> Text me this vehicle
      </div>
      <p className="text-xs text-slate-400 mb-3">No quiz — leave your number and {dealer.name} will reach out.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input required data-testid="quick-lead-name-input" placeholder="Your name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
        <Input required type="tel" data-testid="quick-lead-phone-input" placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
      </div>
      <Input type="email" data-testid="quick-lead-email-input" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-2`} />
      <CommentField value={form.comment} onChange={(comment) => setForm({ ...form, comment })} testId="quick-lead-comment" className="mt-3" />
      <Button type="submit" disabled={busy} data-testid="quick-lead-submit-btn" className="w-full h-10 mt-3 bg-brand/15 hover:bg-brand/25 border border-brand/40 text-brand-soft font-semibold">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Text me"}
      </Button>
    </form>
  );
}

export function NewArrivalsAlert({ dealer }) {
  const [form, setForm] = useState({ customer_name: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const isEmail = form.contact.includes("@");
    setBusy(true);
    try {
      const { data: lead } = await api.post("/public/leads", {
        dealer_id: dealer.id,
        source: "new_arrivals_alert",
        customer_name: form.customer_name,
        email: isEmail ? form.contact : "",
        phone: isEmail ? "" : form.contact,
        message: `Wants to be notified when new vehicles arrive at ${dealer.name}.`,
      });
      saveUnlock(dealer.id, lead);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="new-arrivals-alert" className="rounded-2xl border border-brand/30 bg-gradient-to-r from-brand/10 to-transparent p-6 sm:p-8">
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-brand text-xs font-mono-tech uppercase tracking-widest mb-2">
            <Bell className="h-4 w-4" /> New-arrival alerts
          </div>
          <h3 className="font-heading text-xl sm:text-2xl font-extrabold">Be first to know what hits the lot</h3>
          <p className="text-sm text-slate-400 mt-1">{dealer.name} will ping you when something new arrives that fits.</p>
        </div>
        {done ? (
          <div data-testid="new-arrivals-alert-success" className="flex items-center gap-2 text-emerald-400 font-semibold">
            <CheckCircle2 className="h-5 w-5" /> You're on the list!
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 md:w-[440px]">
            <Input required data-testid="alert-name-input" placeholder="Your name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
            <Input required data-testid="alert-contact-input" placeholder="Mobile or email" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inputCls} />
            <Button type="submit" disabled={busy} data-testid="alert-submit-btn" className="h-10 bg-brand hover:bg-brand/90 font-bold shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Notify me"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
